import React, {useEffect, useRef, useState} from "react";
import {Field} from "react-final-form";
import {shallowEqual, useDispatch, useSelector} from "react-redux";
import Toast from "cogo-toast";
import DateTimeField from "../../../FinalForm/DatePickerField";
import moment from "moment/moment";
import LogMessageSelectField from "../../../FinalForm/api/LogMessageSelectField";
import {FiChevronDown, FiChevronLeft, FiChevronRight, FiChevronUp, FiRefreshCcw, FiSearch, FiSettings, FiX, FiLogIn, FiLogOut, FiMapPin, FiImage, FiCamera, FiFilePlus} from "react-icons/fi";
import {BsFileExcel, BsFiletypeCsv} from "react-icons/bs";
import {DefaultLoader} from "../../../components/DefaultLoader";
import {apiPostEntrancesAll, apiPostExportEntranceAll} from "./Actions";
import EntranceTableV2 from "./EntranceTableV2";
import ApiTariffSelectField from "../../../FinalForm/api/ApiTariffSelectField";
import ApiTurnstileSelectField from "../../../FinalForm/api/ApiTurnstileSelectField";
import ApiZoneSelectField from "../../../FinalForm/api/ApiZoneSelectField";
import FileDownload from "js-file-download";
import {SET_FILTER_RESET} from "../../../reducers/filterEntrance";
import InputField from "../../../FinalForm/InputField";
import {useIsMobile} from "../components/Helper";
import Filter from "../components/Filter";
import {FilterButton} from "../components/Button";
import PageHeader from "../components/PageHeader";

// ─── hooks ────────────────────────────────────────────────────────────────────


// ─── Pagination ───────────────────────────────────────────────────────────────

const PaginationBar = ({page, maxPage, loading, onPrev, onNext, label}) => {
    if (maxPage == null || maxPage < 0) return null;
    return (
        <nav style={{display: "flex", justifyContent: "center", alignItems: "center", gap: "6px", margin: "8px 0"}}>
            <button
                onClick={onPrev} disabled={page <= 0 || loading}
                style={{width: "30px", height: "30px", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "6px", border: "1px solid #e2e8f0", background: "#fff", color: "#94a3b8", cursor: page <= 0 || loading ? "not-allowed" : "pointer", opacity: page <= 0 || loading ? 0.4 : 1}}
            >
                <FiChevronLeft size={13}/>
            </button>
            <span style={{fontSize: "var(--enigoo-fs-sm)", color: "#64748b", padding: "0 6px", minWidth: "120px", textAlign: "center"}}>
                {label && <span style={{color: "#94a3b8", marginRight: "4px"}}>{label}</span>}
                <strong style={{color: "#1e293b"}}>{page + 1}</strong> / {maxPage + 1}
            </span>
            <button
                onClick={onNext} disabled={page >= maxPage || loading}
                style={{width: "30px", height: "30px", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "6px", border: "1px solid #e2e8f0", background: "#fff", color: "#94a3b8", cursor: page >= maxPage || loading ? "not-allowed" : "pointer", opacity: page >= maxPage || loading ? 0.4 : 1}}
            >
                <FiChevronRight size={13}/>
            </button>
        </nav>
    );
};


// ─── chip helpers ─────────────────────────────────────────────────────────────

const chipBase = {borderRadius: "999px", padding: "5px 12px", fontSize: "var(--enigoo-fs-sm)", fontWeight: 500, cursor: "pointer", border: "1px solid #cbd5e1", display: "inline-flex", alignItems: "center", gap: "4px"};
const chipActive = {...chipBase, background: "#2563eb", color: "#fff", border: "1px solid #2563eb", fontWeight: 600};
const chipInactive = {...chipBase, background: "#fff", color: "#475569"};

const toggleArrayValue = (arr, val) => {
    const a = arr ?? [];
    return a.includes(val) ? a.filter(v => v !== val) : [...a, val];
};

// hodnoty odpovídají enumu EntryType na backendu (cz.enigoo.turnstile_api.model.types.EntryType)
const ENTRY_TYPE_OPTIONS = [
    {label: "Vstup",                         value: 1, icon: <FiLogIn size={12}/>},
    {label: "Výstup",                        value: 0, icon: <FiLogOut size={12}/>},
    {label: "Průchod v areálu",              value: 2, icon: <FiMapPin size={12}/>},
    {label: "Přiřazení fotky",               value: 3, icon: <FiImage size={12}/>},
    {label: "Načtení fotky",                 value: 4, icon: <FiCamera size={12}/>},
    {label: "Vytvoření čipu bez fotografie", value: 5, icon: <FiFilePlus size={12}/>},
];

// ─── main ─────────────────────────────────────────────────────────────────────

const EntranceV2 = () => {
    const [data, setData]               = useState(null);
    const [apiPage, setApiPage]         = useState(0);
    const [loading, setLoading]         = useState(false);
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const isMobile                      = useIsMobile();
    const formRef                       = useRef(null);

    const {activeAreal} = useSelector(s => s.user);
    const {filterEntrance} = useSelector(s => ({filterEntrance: s.filterEntrance}), shallowEqual);
    const dispatch = useDispatch();

    const filterData = filterEntrance.filterEntranceData;

    const advancedCount = [filterData?.result]
        .filter(v => v != null && v !== "").length
        + ((filterData?.tariffId?.length   ?? 0) > 0 ? 1 : 0)
        + ((filterData?.type?.length        ?? 0) > 0 ? 1 : 0)
        + ((filterData?.ticketType?.length  ?? 0) > 0 ? 1 : 0)
        + (filterData?.eventId   != null && filterData?.eventId   !== "" ? 1 : 0)
        + (filterData?.ticketName != null && filterData?.ticketName !== "" ? 1 : 0)
        + (filterData?.ticketNote != null && filterData?.ticketNote !== "" ? 1 : 0);

    const convertDate = (date) => date ? date.replace("T", " ") + ":00" : null;

    const getData = (filterValues, page) => {
        setLoading(true);
        const postData = {
            ...filterValues,
            page,
            dateFrom: convertDate(filterValues?.dateFrom),
            dateTo:   convertDate(filterValues?.dateTo),
        };

        if (filterValues.typeData === "export" || filterValues.typeData === "csv") {
            const format = filterValues.typeData === "export" ? "xls" : "csv";
            apiPostExportEntranceAll(activeAreal.id, {...postData, format}, (response) => {
                const isXls = format === "xls";
                const blob = new Blob([response.data], {type: isXls ? "application/vnd.ms-excel" : "text/csv;charset=utf-8"});
                const disposition = response.headers["content-disposition"] ?? "";
                const fileName = isXls
                    ? (disposition.replace("attachment; filename=", "") || "export.xls")
                    : (disposition.split("filename=")[1]?.replace(/"/g, "") ?? "export.csv");
                FileDownload(blob, fileName);
                setLoading(false);
            }, () => { Toast.error("Při stahování průchodů nastala chyba."); setLoading(false); });
        } else {
            apiPostEntrancesAll(activeAreal.id, postData, (d) => {
                setData(d ?? null);
                setLoading(false);
            }, () => { Toast.error("Při načítání průchodů nastala chyba."); setData(null); setLoading(false); });
        }
    };

    const hasData = !loading && data?.data?.length > 0;

    return (
        <>
            {activeAreal && (
                <>
                    <PageHeader crumbs={[{label: "Průchody"}]}>
                        {hasData && (
                            <div style={{display: "flex", gap: "12px", fontSize: "var(--enigoo-fs-sm)", color: "#64748b"}}>
                                <span>Celkem: <strong style={{color: "#1e293b"}}>{data.maxCount}</strong></span>
                                <span>Na stránce: <strong style={{color: "#1e293b"}}>{data.countOnActualPage}</strong></span>
                            </div>
                        )}
                    </PageHeader>

                    <Filter
                        onSubmit={(values) => {
                            setApiPage(0);
                            dispatch({type: "SET_FILTER_ENTRANCE", payload: values});
                            getData(values, 0);
                        }}
                        initialValues={filterData}
                        formRef={formRef}
                        loading={loading}
                        extraButtons={({form, submit}) => (
                            <>
                                <FilterButton
                                    onClick={() => { form.change("typeData", "csv"); submit(); }}
                                    disabled={loading}
                                    text="CSV"
                                    icon={<BsFiletypeCsv size={13}/>}
                                    color="outline"
                                />
                                <FilterButton
                                    onClick={() => { form.change("typeData", "export"); submit(); }}
                                    disabled={loading}
                                    text="Excel"
                                    icon={<BsFileExcel size={13}/>}
                                    color="outline"
                                />
                            </>
                        )}
                    >
                        {({values, form, submit}) => {
                            const hasAnyFilter = values.bc || values.withoutLog != null
                                || values.dateFrom || values.dateTo
                                || (values.turnstileId?.length ?? 0) > 0
                                || (values.zoneId?.length ?? 0) > 0
                                || (values.ticketTicketType?.length ?? 0) > 0
                                || values.eventId || values.ticketName || values.ticketNote
                                || advancedCount > 0;

                            return (
                                <>
                                    {/* BC search */}
                                    <div style={{position: "relative", marginBottom: "8px"}}>
                                        <FiSearch size={15} style={{position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none"}}/>
                                        <Field name="bc">
                                            {({input}) => (
                                                <input
                                                    {...input}
                                                    type="text"
                                                    placeholder="Hledat podle BC..."
                                                    onKeyDown={e => e.key === "Enter" && submit()}
                                                    style={{width: "100%", padding: "9px 14px 9px 38px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "var(--enigoo-fs-md)", background: "#fff", color: "#1e293b", outline: "none", boxSizing: "border-box"}}
                                                />
                                            )}
                                        </Field>
                                    </div>

                                    {/* always-visible fields */}
                                    <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "8px", marginBottom: "8px"}}>
                                        <DateTimeField name={"dateFrom"} isMandatory={false} valueToCompare={values?.dateTo}
                                            validateFun={(d1, d2) => moment(d1).isAfter(moment(d2)) ? "Datum od nemůže být po datu do" : ""}
                                            label={"Datum od"} type={"from"} placeholder={"Datum od"} showTimeSelect onEnter={submit}/>
                                        <DateTimeField name={"dateTo"} isMandatory={false} valueToCompare={values?.dateFrom}
                                            validateFun={(d1, d2) => moment(d2).isAfter(moment(d1)) ? "Datum od nemůže být po datu do" : ""}
                                            label={"Datum do"} type={"to"} placeholder={"Datum do"} showTimeSelect onEnter={submit}/>
                                        <ApiTurnstileSelectField name={"turnstileId"} isMandatory={false} isMulti onEnter={submit}/>
                                        <ApiZoneSelectField     name={"zoneId"}      isMandatory={false} isMulti onEnter={submit}/>
                                    </div>

                                    {/* quick chips row */}
                                    <div style={{display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px", marginBottom: "8px"}}>
                                        <Field name="withoutLog">
                                            {({input}) => (
                                                <button type="button"
                                                    onClick={() => input.onChange(input.value === true ? null : true)}
                                                    style={input.value === true ? chipActive : chipInactive}
                                                >
                                                    Jen úspěšné
                                                </button>
                                            )}
                                        </Field>

                                        <div style={{width: "1px", height: "18px", background: "#e2e8f0"}}/>

                                        <Field name="ticketTicketType">
                                            {({input}) => (<>
                                                {[{label: "Servisní", value: 0}, {label: "Běžná", value: 1}, {label: "Permanentka", value: 2}].map(opt => {
                                                    const active = (input.value ?? []).includes(opt.value);
                                                    return (
                                                        <button key={opt.value} type="button"
                                                            onClick={() => input.onChange(toggleArrayValue(input.value, opt.value))}
                                                            style={active ? chipActive : chipInactive}
                                                        >
                                                            {opt.label}
                                                        </button>
                                                    );
                                                })}
                                            </>)}
                                        </Field>

                                        <div style={{width: "1px", height: "18px", background: "#e2e8f0"}}/>

                                        <button type="button"
                                            onClick={() => setAdvancedOpen(v => !v)}
                                            style={{...chipInactive, color: advancedCount > 0 ? "#2563eb" : "#475569", border: `1px solid ${advancedCount > 0 ? "#bfdbfe" : "#cbd5e1"}`, background: advancedCount > 0 ? "#eff6ff" : "#fff"}}
                                        >
                                            <FiSettings size={11}/>
                                            Pokročilé
                                            {advancedCount > 0 && (
                                                <span style={{background: "#2563eb", color: "#fff", fontSize: "var(--enigoo-fs-2xs)", fontWeight: 700, borderRadius: "999px", padding: "1px 6px"}}>
                                                    {advancedCount}
                                                </span>
                                            )}
                                            {advancedOpen ? <FiChevronUp size={11}/> : <FiChevronDown size={11}/>}
                                        </button>

                                        {hasAnyFilter && (
                                            <button type="button"
                                                onClick={() => { dispatch({type: SET_FILTER_RESET}); form.reset(); }}
                                                style={{marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: "var(--enigoo-fs-xs)", display: "flex", alignItems: "center", gap: "3px"}}
                                            >
                                                <FiX size={11}/> Zrušit filtry
                                            </button>
                                        )}
                                    </div>

                                    {/* advanced fields */}
                                    {advancedOpen && (
                                        <div style={{padding: "12px", marginBottom: "8px"}}>
                                            <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "8px"}}>
                                                <ApiTariffSelectField  name={"tariffId"}    isMandatory={false} isMulti onEnter={submit}/>
                                                <LogMessageSelectField name={"result"}      onEnter={submit}/>
                                                <InputField name={"eventId"}    label={"Id události"}           isMandatory={false} inputType={"number"} placeholder={"Id události"}           onEnter={submit}/>
                                                <InputField name={"ticketName"} label={"Jméno na vstupence"}    isMandatory={false} placeholder={"Jméno na vstupence"}   onEnter={submit}/>
                                                <InputField name={"ticketNote"} label={"Poznámka na vstupence"} isMandatory={false} placeholder={"Poznámka na vstupence"} onEnter={submit}/>
                                                <Field name="type">
                                                    {({input}) => (
                                                        <div style={{gridColumn: "1 / -1"}}>
                                                            <label style={{display: "block", fontSize: "var(--enigoo-fs-xs)", fontWeight: 600, color: "#64748b", marginBottom: "4px"}}>Typ průchodu</label>
                                                            <div style={{display: "flex", flexWrap: "wrap", gap: "6px"}}>
                                                                {ENTRY_TYPE_OPTIONS.map(opt => {
                                                                    const active = (input.value ?? []).includes(opt.value);
                                                                    return (
                                                                        <button key={opt.value} type="button"
                                                                            onClick={() => input.onChange(toggleArrayValue(input.value, opt.value))}
                                                                            style={active ? chipActive : chipInactive}
                                                                        >
                                                                            {opt.icon}{opt.label}
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    )}
                                                </Field>
                                                <Field name="ticketType">
                                                    {({input}) => (
                                                        <div>
                                                            <label style={{display: "block", fontSize: "var(--enigoo-fs-xs)", fontWeight: 600, color: "#64748b", marginBottom: "4px"}}>Sleva vstupenky</label>
                                                            <div style={{display: "flex", gap: "6px"}}>
                                                                {[{label: "Děti", value: 1}, {label: "Zlevněná", value: 2}].map(opt => {
                                                                    const active = (input.value ?? []).includes(opt.value);
                                                                    return (
                                                                        <button key={opt.value} type="button"
                                                                            onClick={() => input.onChange(toggleArrayValue(input.value, opt.value))}
                                                                            style={active ? chipActive : chipInactive}
                                                                        >
                                                                            {opt.label}
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    )}
                                                </Field>
                                            </div>
                                        </div>
                                    )}
                                </>
                            );
                        }}
                    </Filter>

                    {/* API pagination — top */}
                    {hasData && (
                        <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px", flexWrap: "wrap", gap: "6px"}}>
                            <span style={{fontSize: "var(--enigoo-fs-xs)", color: "#94a3b8"}}>
                                API stránka — celkem <strong style={{color: "#475569"}}>{data.maxCount}</strong> záznamů
                            </span>
                            <PaginationBar
                                label="API:"
                                page={apiPage} maxPage={data.maxPage} loading={loading}
                                onPrev={() => { const p = apiPage - 1; setApiPage(p); getData(filterEntrance.filterEntranceData, p); }}
                                onNext={() => { const p = apiPage + 1; setApiPage(p); getData(filterEntrance.filterEntranceData, p); }}
                            />
                        </div>
                    )}

                    {/* body */}
                    {loading
                        ? <DefaultLoader text={"Načítám průchody..."}/>
                        : data?.data
                            ? <EntranceTableV2 data={data.data}/>
                            : null
                    }
                </>
            )}
        </>
    );
};

export default EntranceV2;
